/**
 * Générateur — Il écrit plusieurs scripts fonctionnant en parallèle pour
 * gérer des interactions et créer des jeux.
 * Attendu ID : 253 | Niveau : 3ème | Domaine : Algorithmique (d21, O4 niv. 3)
 *
 * Source doc prof (Créé n°22 — le jeu du chat et de la souris). Plan
 * auto-validé 2026-07-18 : « jeu » (QCM tournant : qui pilote qui, pourquoi
 * trois scripts séparés, que casserait une initialisation dans la boucle),
 * « roles » (relier chaque script à son rôle dans le jeu).
 */

import { randomChoice, randInt, shuffle, makeQcm, makeRelier } from '../../../utils';

const ATTENDU_ID = 253;
const L_JEU = 'Le jeu du chat et de la souris';
const L_ROLES = 'Le rôle de chaque script';
const opts = (label) => ({ niveau: '3eme', matiere: 'maths', attendu: ATTENDU_ID, label });

const contexteJeu = (v, t) => '<p>Un jeu comporte deux lutins et TROIS scripts qui démarrent tous quand le '
    + 'drapeau vert est cliqué. Souris — script 1 : répéter indéfiniment [aller à la position du pointeur]. '
    + `Chat — script 2 : répéter indéfiniment [avancer de ${v} vers Souris]. Chat — script 3 : mettre Score `
    + `à 0 ; répéter indéfiniment [si (touche Souris ?) alors ajouter 1 à Score ; attendre ${t} seconde${t > 1 ? 's' : ''}].</p>`;

// ═════════ « jeu » — QCM tournant ═════════

const genJeuQcm = () => {
    const v = randomChoice([3, 5, 8, 10]);
    const t = randomChoice([1, 2]);
    const variante = randInt(0, 2);
    let question; let correct; let choix;
    if (variante === 0) {
        question = 'Qui pilote la Souris, et qui pilote le Chat ?';
        correct = 'Le joueur pilote la Souris (elle suit le pointeur) ; l\'ordinateur pilote le Chat';
        choix = [correct,
            'Le joueur pilote le Chat ; l\'ordinateur pilote la Souris',
            'Le joueur pilote les deux lutins',
            'L\'ordinateur pilote les deux lutins'];
    } else if (variante === 1) {
        question = 'Pourquoi trois scripts SÉPARÉS plutôt qu\'un seul ?';
        correct = 'Les trois actions (déplacement du joueur, poursuite, comptage) doivent se dérouler EN MÊME TEMPS';
        choix = [correct,
            'Un script ne peut pas contenir plus de trois blocs',
            'C\'est seulement pour que le programme soit plus joli',
            'Parce que chaque lutin ne peut avoir qu\'un seul bloc chapeau'];
    } else {
        question = 'Que se passerait-il si « mettre Score à 0 » était placé DANS la boucle du script 3 ?';
        correct = 'Le Score serait remis à 0 à chaque tour : il ne progresserait jamais';
        choix = [correct,
            'Rien : le Score compterait exactement pareil',
            'Le jeu deviendrait plus rapide',
            'Le Chat s\'arrêterait de poursuivre la Souris'];
    }
    const payload = makeQcm(choix, correct, `${contexteJeu(v, t)}<p><b>${question}</b></p>`, opts(L_JEU));
    payload._meta.params = { variante, v, t };
    return payload;
};

// ═════════ « roles » — relier (refonte bilan prof D3, 2026-07-27) ═════════
// Les scripts sont désormais AFFICHÉS en SVG fidèle à l'apparence Scratch
// (blocs chapeau jaunes, contrôle orange en C, mouvement bleu, variables
// orange foncé, capteurs bleu clair) — l'ancien relier textuel n'était pas
// fidèle aux blocs réels. Gabarit : 2 scripts numérotés ① ② piochés dans un
// pool de 6, 2 rôles corrects + 2-3 leurres (rôles plausibles que ces
// scripts ne réalisent PAS), makeRelier + cibles_leurres.

const SCRATCH_COLORS = {
    evt: '#FFD500', ctl: '#FFAB19', mvt: '#4C97FF',
    varb: '#FF8C1A', sens: '#5CB1D6', app: '#9966FF',
};

const BLOC_H = 22;
const BLOC_GAP = 2;
const SCRIPT_W = 260;

/** Un bloc simple (rect arrondi + label ; drapeau vert si flag). */
const svgBloc = (x, y, w, color, label, flag = false) => {
    const texte = flag
        ? `<polygon points="${x + 8},${y + 6} ${x + 18},${y + 9} ${x + 8},${y + 13}" fill="#4CBF56" stroke="#3a9b43" stroke-width="1"/>`
          + `<line x1="${x + 8}" y1="${y + 6}" x2="${x + 8}" y2="${y + 17}" stroke="#8a6d1f" stroke-width="1.5"/>`
          + `<text x="${x + 23}" y="${y + BLOC_H / 2 + 4}" font-family="Verdana,sans-serif" font-size="10.5" fill="#4a3d00">${label}</text>`
        : `<text x="${x + 8}" y="${y + BLOC_H / 2 + 4}" font-family="Verdana,sans-serif" font-size="10.5" fill="${color === SCRATCH_COLORS.evt ? '#4a3d00' : '#fff'}">${label}</text>`;
    return `<rect x="${x}" y="${y}" width="${w}" height="${BLOC_H}" rx="${flag ? 10 : 4}" fill="${color}" stroke="rgba(0,0,0,0.25)" stroke-width="1"/>${texte}`;
};

/** Hauteur totale d'une liste de blocs (répéter = en-tête + contenu + pied). */
const hauteurBlocs = (blocs) => blocs.reduce((h, b) => {
    if (b.type === 'repeat') return h + BLOC_H + BLOC_GAP + hauteurBlocs(b.inner) + 10 + BLOC_GAP;
    return h + BLOC_H + BLOC_GAP;
}, 0);

/** Rend une liste de blocs à partir de (x, y) ; retourne { svg, y } final. */
const svgListeBlocs = (blocs, x, y, w) => {
    let out = '';
    for (const b of blocs) {
        if (b.type === 'repeat') {
            const innerH = hauteurBlocs(b.inner);
            out += svgBloc(x, y, w, SCRATCH_COLORS.ctl, b.label);
            // Colonne gauche (le « C » du bloc contrôle) + pied
            out += `<rect x="${x}" y="${y + BLOC_H}" width="12" height="${innerH + BLOC_GAP}" fill="${SCRATCH_COLORS.ctl}" stroke="rgba(0,0,0,0.25)" stroke-width="1"/>`;
            const inner = svgListeBlocs(b.inner, x + 14, y + BLOC_H + BLOC_GAP, w - 14);
            out += inner.svg;
            const footY = y + BLOC_H + BLOC_GAP + innerH;
            out += `<rect x="${x}" y="${footY}" width="${w * 0.55}" height="10" rx="4" fill="${SCRATCH_COLORS.ctl}" stroke="rgba(0,0,0,0.25)" stroke-width="1"/>`;
            y = footY + 10 + BLOC_GAP;
        } else {
            out += svgBloc(x, y, w, b.color, b.label, b.flag);
            y += BLOC_H + BLOC_GAP;
        }
    }
    return { svg: out, y };
};

/** Pool de scripts paramétrables : blocs + rôle correct. */
const buildScriptsPool = (v, t, d) => [
    {
        id: 'souris_pointeur',
        role: 'déplacer la Souris là où pointe le joueur',
        blocs: [
            { flag: true, color: SCRATCH_COLORS.evt, label: 'quand le drapeau vert est cliqué' },
            { type: 'repeat', label: 'répéter indéfiniment', inner: [
                { color: SCRATCH_COLORS.mvt, label: 'aller à la position du pointeur' },
            ] },
        ],
    },
    {
        id: 'chat_poursuite',
        role: 'faire poursuivre la Souris par le Chat',
        blocs: [
            { flag: true, color: SCRATCH_COLORS.evt, label: 'quand le drapeau vert est cliqué' },
            { type: 'repeat', label: 'répéter indéfiniment', inner: [
                { color: SCRATCH_COLORS.mvt, label: `avancer de ${v} vers Souris` },
            ] },
        ],
    },
    {
        id: 'score_compte',
        role: 'compter les fois où le Chat attrape la Souris',
        blocs: [
            { flag: true, color: SCRATCH_COLORS.evt, label: 'quand le drapeau vert est cliqué' },
            { color: SCRATCH_COLORS.varb, label: 'mettre Score à 0' },
            { type: 'repeat', label: 'répéter indéfiniment', inner: [
                { color: SCRATCH_COLORS.sens, label: 'si (touche Souris ?) alors' },
                { color: SCRATCH_COLORS.varb, label: 'ajouter 1 à Score' },
                { color: SCRATCH_COLORS.ctl, label: `attendre ${t} seconde${t > 1 ? 's' : ''}` },
            ] },
        ],
    },
    {
        id: 'rebond_bord',
        role: 'garder le lutin à l\'intérieur de l\'écran',
        blocs: [
            { flag: true, color: SCRATCH_COLORS.evt, label: 'quand le drapeau vert est cliqué' },
            { type: 'repeat', label: 'répéter indéfiniment', inner: [
                { color: SCRATCH_COLORS.mvt, label: 'rebondir si le bord est atteint' },
            ] },
        ],
    },
    {
        id: 'fin_partie',
        role: 'afficher le résultat à la fin de la partie',
        blocs: [
            { flag: true, color: SCRATCH_COLORS.evt, label: 'quand je reçois [ Fin ]' },
            { color: SCRATCH_COLORS.app, label: 'dire (Score) pendant 2 secondes' },
        ],
    },
    {
        id: 'chrono',
        role: 'faire défiler le temps restant de la partie',
        blocs: [
            { flag: true, color: SCRATCH_COLORS.evt, label: 'quand le drapeau vert est cliqué' },
            { color: SCRATCH_COLORS.varb, label: `mettre Chrono à ${d}` },
            { type: 'repeat', label: 'répéter indéfiniment', inner: [
                { color: SCRATCH_COLORS.ctl, label: 'attendre 1 seconde' },
                { color: SCRATCH_COLORS.varb, label: 'retirer 1 à Chrono' },
            ] },
        ],
    },
];

/** Rôles plausibles que les scripts du pool ne réalisent JAMAIS. */
const LEURRES_ROLES = [
    'faire rebondir la balle sur les raquettes',
    'augmenter la vitesse à chaque niveau',
    'faire gagner une vie au joueur',
    'changer le décor à chaque partie',
    'choisir un lutin au hasard au démarrage',
];

const svgDeuxScripts = (s1, s2) => {
    const margin = 14;
    const numW = 24;
    const colW = SCRIPT_W + numW + 10;
    const h1 = hauteurBlocs(s1.blocs);
    const h2 = hauteurBlocs(s2.blocs);
    const H = Math.max(h1, h2) + 2 * margin + 8;
    const W = 2 * colW + 3 * margin;
    const panneau = (s, idx, ox) => {
        const badge = `<circle cx="${ox + 12}" cy="${margin + 12}" r="11" fill="#1E3A8A"/>`
            + `<text x="${ox + 12}" y="${margin + 16}" font-family="Verdana,sans-serif" font-size="12" fill="#fff" text-anchor="middle">${idx}</text>`;
        const blocs = svgListeBlocs(s.blocs, ox + numW, margin + 4, SCRIPT_W);
        return badge + blocs.svg;
    };
    return `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 ${W} ${H}" width="${W}" height="${H}">`
        + `<rect width="${W}" height="${H}" fill="#f6f8fa" rx="8"/>`
        + panneau(s1, '1', margin)
        + panneau(s2, '2', margin * 2 + colW)
        + '</svg>';
};

const svgToDataUrl253 = (svgString) =>
    `data:image/svg+xml;utf8,${encodeURIComponent(svgString)}`;

const genRolesRelier = () => {
    const v = randomChoice([3, 5, 8, 10]);
    const t = randomChoice([1, 2]);
    const d = randomChoice([30, 60, 90]);
    const [s1, s2] = shuffle(buildScriptsPool(v, t, d)).slice(0, 2);

    const pairs = [
        { source: 'Script 1', target: s1.role },
        { source: 'Script 2', target: s2.role },
    ];
    const leurres = shuffle(LEURRES_ROLES).slice(0, randInt(2, 3))
        .map(r => ({ target: r, code: 'role_plausible_non_realise' }));

    const payload = makeRelier(pairs,
        '<p>Deux scripts d\'un jeu Scratch sont affichés ci-dessous, numérotés 1 et 2. '
        + 'Plusieurs scripts d\'un même jeu tournent EN PARALLÈLE.</p>{{element:0}}'
        + '<p><b>Relie chaque script à son rôle dans le jeu.</b> '
        + 'Attention : certains rôles ne correspondent à aucun des deux scripts.</p>',
        {
            ...opts(L_ROLES),
            cibles_leurres: leurres,
            elements_additionnels: {
                version: 1,
                elements: [{ type: 'image', config: { imageData: svgToDataUrl253(svgDeuxScripts(s1, s2)) } }],
            },
        });
    payload._meta.params = {
        scripts: [s1.id, s2.id], v, t, d,
        distracteurs: Object.fromEntries(leurres.map(l => [l.target, l.code])),
    };
    return payload;
};

const GENERATORS = {
    jeu_qcm_unique: genJeuQcm,
    roles_relier: genRolesRelier,
};
const FORMAT_LABELS = { qcm_unique: 'QCM', relier: 'Relier' };
const buildFormats = (prefix) =>
    Object.keys(GENERATORS).filter(k => k.startsWith(`${prefix}_`))
        .map(k => ({ key: k, label: FORMAT_LABELS[k.slice(prefix.length + 1)] || k }));
export const CATEGORIES = [
    { id: 'jeu', label: L_JEU, formatsList: buildFormats('jeu') },
    { id: 'roles', label: L_ROLES, formatsList: buildFormats('roles') },
];
export const generate = (typeExercice) => {
    let key;
    if (typeExercice && GENERATORS[typeExercice]) key = typeExercice;
    else if (typeExercice) {
        const matching = Object.keys(GENERATORS).filter(k => k.startsWith(typeExercice) || k.includes(typeExercice));
        key = matching.length ? randomChoice(matching) : randomChoice(Object.keys(GENERATORS));
    } else key = randomChoice(Object.keys(GENERATORS));
    const payload = GENERATORS[key]();
    payload._meta.generatorKey = key;
    return payload;
};
